import {useEffect, useRef, useState, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {ROUTES} from '../../../../../_gori/constants'
import {isValidGoriSignatureHash} from '../../utils/validation'
import {LocationState} from '../../../../../types/locationTypes'
import {CameraView} from './CameraView'
import {NavigationControls, ManualCaptureButton, ErrorFallback} from './NavigationControls'
import {useLabelDetector, BarcodeDetectionResult} from './LabelDetector'
import {useImageProcessor} from './ImageProcessor'
import {clearLabelSessionStorage} from '../../utils/sessionStorageUtils'

const CONFIG = {
  DETECTION_INTERVAL_MS: 500, // Run barcode detection every 500ms
  CAPTURE_DELAY_MS: 1000, // Wait 1 second after detection before capturing to ensure stability
  VIDEO_WIDTH: 1920, // Ideal video width for high-quality capture
  VIDEO_HEIGHT: 1080, // Ideal video height for high-quality capture
  MAX_DETECTION_ATTEMPTS: 120, // Stop after 60 seconds (120 * 500ms)
}

type DetectionStatus = 'searching' | 'detected' | 'capturing'

export function GoriSignatureTakePhoto() {
  const navigate = useNavigate()
  const location = useLocation() as {state?: LocationState}
  const intl = useIntl()
  const hash = location.state?.hash

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const detectionIntervalRef = useRef<number | null>(null)
  const captureTimeoutRef = useRef<number | null>(null)
  const isProcessingRef = useRef(false)
  const isMountedRef = useRef(true)
  const detectionAttemptsRef = useRef(0)

  const [detectionStatus, setDetectionStatus] = useState<DetectionStatus>('searching')
  const [hasCamera, setHasCamera] = useState(true)

  const {processAndStore} = useImageProcessor(hash)

  useEffect(() => {
    isMountedRef.current = true
    if (!hash || !isValidGoriSignatureHash(hash)) {
      navigate(ROUTES.GORI_SIGNATURE.BASE, {replace: true})
    }
    return () => {
      isMountedRef.current = false
    }
  }, [hash, navigate])

  const resetToSearchingRef = useRef<(() => void) | null>(null)

  const handleBarcodeDetected = useCallback(
    (result: BarcodeDetectionResult): void => {
      if (isProcessingRef.current || captureTimeoutRef.current) return

      isProcessingRef.current = true
      setDetectionStatus('detected')

      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current)
        detectionIntervalRef.current = null
      }

      captureTimeoutRef.current = window.setTimeout(async () => {
        if (!isMountedRef.current || !videoRef.current || !canvasRef.current || !hash) return

        setDetectionStatus('capturing')

        const video = videoRef.current
        const canvas = canvasRef.current
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          if (isMountedRef.current) {
            resetToSearchingRef.current?.()
          }
          return
        }

        ctx.drawImage(video, 0, 0)

        const result = await processAndStore(canvas)

        if (!isMountedRef.current) return

        if (!result.success) {
          if (hash) clearLabelSessionStorage(hash)
          toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_LABEL_DETECTION_ERROR'}))
          resetToSearchingRef.current?.()
          return
        }

        navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`)
      }, CONFIG.CAPTURE_DELAY_MS)
    },
    [hash, intl, navigate, processAndStore]
  )

  const {detect, hasDetector} = useLabelDetector({
    videoRef,
    onDetected: handleBarcodeDetected,
    isActive: true,
  })

  const detectRef = useRef(detect)
  detectRef.current = detect

  const startDetectionInterval = useCallback(() => {
    // Clear any existing interval to prevent race conditions
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current)
      detectionIntervalRef.current = null
    }

    detectionAttemptsRef.current = 0

    detectionIntervalRef.current = window.setInterval(() => {
      // Skip if already processing
      if (isProcessingRef.current) return

      detectionAttemptsRef.current += 1
      if (detectionAttemptsRef.current >= CONFIG.MAX_DETECTION_ATTEMPTS) {
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current)
          detectionIntervalRef.current = null
        }
        if (isMountedRef.current) {
          toast.info(intl.formatMessage({id: 'GORI_SIGNATURE_DETECTION_TIMEOUT'}))
        }
        return
      }
      detectRef.current()
    }, CONFIG.DETECTION_INTERVAL_MS)
  }, [intl])

  const startDetection = useCallback(() => {
    startDetectionInterval()
  }, [startDetectionInterval])

  const resetToSearching = useCallback(() => {
    isProcessingRef.current = false
    setDetectionStatus('searching')

    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current)
      captureTimeoutRef.current = null
    }

    startDetectionInterval()
  }, [startDetectionInterval])

  resetToSearchingRef.current = resetToSearching

  const handleManualCapture = useCallback(async () => {
    if (isProcessingRef.current || !videoRef.current || !canvasRef.current || !isMountedRef.current)
      return

    setDetectionStatus('capturing')
    isProcessingRef.current = true

    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current)
      detectionIntervalRef.current = null
    }
    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current)
      captureTimeoutRef.current = null
    }

    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      resetToSearching()
      return
    }

    ctx.drawImage(video, 0, 0)

    const result = await processAndStore(canvas)

    if (!isMountedRef.current) return

    if (!result.success) {
      if (hash) clearLabelSessionStorage(hash)
      toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_LABEL_DETECTION_ERROR'}))
      resetToSearching()
      return
    }

    navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`)
  }, [hash, intl, navigate, processAndStore, resetToSearching])

  // Keyboard navigation: Space or Enter to capture
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === ' ' || e.key === 'Enter') && detectionStatus !== 'capturing') {
        e.preventDefault()
        handleManualCapture()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleManualCapture, detectionStatus])

  const startCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(intl.formatMessage({id: 'GORI_SIGNATURE_CAMERA_ERROR'}))
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: {ideal: CONFIG.VIDEO_WIDTH},
          height: {ideal: CONFIG.VIDEO_HEIGHT},
        },
      })

      streamRef.current = mediaStream

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current?.play()
            startDetection()
          } catch (err) {
            if (isMountedRef.current) {
              setHasCamera(false)
              toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_CAMERA_ERROR'}))
            }
          }
        }
      }
    } catch {
      if (isMountedRef.current) {
        setHasCamera(false)
        toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_CAMERA_ERROR'}))
      }
    }
  }, [intl, startDetection])

  useEffect(() => {
    startCamera()
    return () => {
      if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current)
      if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!hasCamera) {
    return <ErrorFallback errorMessageKey='GORI_SIGNATURE_CAMERA_UNAVAILABLE' hash={hash} />
  }

  if (!hasDetector) {
    return (
      <ErrorFallback errorMessageKey='GORI_SIGNATURE_BARCODE_DETECTOR_UNAVAILABLE' hash={hash} />
    )
  }

  return (
    <div className='gori-signature-take-photo min-vh-100 d-flex flex-column align-items-center justify-content-center p-4'>
      <NavigationControls
        onManualCapture={handleManualCapture}
        isCapturing={detectionStatus === 'capturing'}
        hash={hash}
      />

      <CameraView ref={videoRef} detectionStatus={detectionStatus} />
      <canvas ref={canvasRef} style={{display: 'none'}} />

      <ManualCaptureButton
        onManualCapture={handleManualCapture}
        isCapturing={detectionStatus === 'capturing'}
        hash={hash}
      />
    </div>
  )
}
